import React, { useEffect, useState } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSync,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import ProductoTable from "./components/ProductoTable";
import ProductoModal from "./components/ProductoModal";

function ProductoList() {
  const [productos, setProductos] = useState([]);
  const [productosFiltrados, setProductosFiltrados] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("TODAS");
  const [busqueda, setBusqueda] = useState("");

  const [mostrarModal, setMostrarModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);

  const [productoActual, setProductoActual] = useState({
    id: null,
    nombre: "",
    categoriaId: "",
    precio: 0,
    costo: 0, // 🔥 NUEVO
    bloqueado: false,
    ingredientes: "",
    imagenFile: null,
    imagen: null,
  });

  /* ===============================
     CARGA INICIAL
  =============================== */
  useEffect(() => {
    obtenerProductos();
    obtenerCategorias();
  }, []);

  useEffect(() => {
    filtrarProductos();
  }, [categoriaSeleccionada, busqueda, productos]);

  /* ===============================
     API CALLS
  =============================== */

  const obtenerProductos = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/productos");
      setProductos(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("❌ Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  const obtenerCategorias = async () => {
    try {
      const res = await axios.get("/categorias/activas");
      setCategorias(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("❌ Error al cargar categorías");
    }
  };

  /* ===============================
     FILTROS
  =============================== */

  const filtrarProductos = () => {
    let filtrados = productos;

    if (categoriaSeleccionada !== "TODAS") {
      filtrados = filtrados.filter(
        (p) => p.categoria?.id === Number(categoriaSeleccionada)
      );
    }

    if (busqueda.trim() !== "") {
      filtrados = filtrados.filter((p) =>
        p.nombre.toLowerCase().includes(busqueda.toLowerCase())
      );
    }

    setProductosFiltrados(filtrados);
  };

  /* ===============================
     MODAL
  =============================== */

  const abrirModalNuevo = () => {
    setProductoActual({
      id: null,
      nombre: "",
      categoriaId: "",
      precio: 0,
      costo: 0, // 🔥 LIMPIAR COSTO
      bloqueado: false,
      ingredientes: "",
      imagenFile: null,
      imagen: null,
    });

    setModoEdicion(false);
    setMostrarModal(true);
  };

  const abrirModalEditar = (producto) => {
    setProductoActual({
      id: producto.id,
      nombre: producto.nombre,
      categoriaId: producto.categoria?.id || "",
      precio: producto.precio || 0,
      costo: producto.costo || 0, // 🔥 CARGAR COSTO
      bloqueado: producto.bloqueado ?? false,
      ingredientes: producto.ingredientes || "",
      imagenFile: null,
      imagen: producto.imagenFinal || null,
    });

    setModoEdicion(true);
    setMostrarModal(true);
  };

  const actualizarCampo = (campo, valor) => {
    setProductoActual((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  /* ===============================
     CRUD
  =============================== */

  const guardarProducto = async () => {
    if (
      !productoActual.nombre ||
      !productoActual.categoriaId ||
      productoActual.precio <= 0
    ) {
      toast.warning("⚠️ Completa los campos obligatorios");
      return;
    }

    if (productoActual.costo > productoActual.precio) {
      toast.error("❌ El costo no puede ser mayor que el precio");
      return;
    }

    try {
      const formData = new FormData();

      const productoPayload = {
        nombre: productoActual.nombre,
        precio: Number(productoActual.precio),
        costo: Number(productoActual.costo), // 🔥 ENVIAR COSTO
        bloqueado: productoActual.bloqueado,
        ingredientes: productoActual.ingredientes,
        categoria: {
          id: Number(productoActual.categoriaId),
        },
      };

      formData.append(
        "producto",
        new Blob([JSON.stringify(productoPayload)], {
          type: "application/json",
        })
      );

      if (productoActual.imagenFile) {
        formData.append("imagen", productoActual.imagenFile);
      }

      if (modoEdicion && productoActual.id) {
        await axios.put(`/productos/${productoActual.id}`, formData);
        toast.success("✅ Producto actualizado");
      } else {
        await axios.post("/productos", formData);
        toast.success("✅ Producto creado");
      }

      setMostrarModal(false);
      obtenerProductos();

    } catch (error) {
      console.error(error);
      toast.error("❌ Error guardando producto");
    }
  };

  const eliminarProducto = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este producto?")) return;
    try {
      await axios.delete(`/productos/${id}`);
      toast.success("🗑️ Producto eliminado");
      obtenerProductos();
    } catch {
      toast.error("❌ No se pudo eliminar el producto");
    }
  };

  const toggleEstadoProducto = async (producto) => {
    try {
      await axios.patch(`/productos/${producto.id}/bloqueado`);
      toast.success(
        producto.bloqueado
          ? "✅ Producto habilitado"
          : "🚫 Producto bloqueado"
      );
      obtenerProductos();
    } catch {
      toast.error("❌ No se pudo cambiar el estado");
    }
  };

  /* ===============================
     RENDER
  =============================== */

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 dark:bg-slate-900/90 rounded-2xl shadow-xl p-6 mt-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-amber-500">
            Gestión de Productos
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={categoriaSeleccionada}
              onChange={(e) => setCategoriaSeleccionada(e.target.value)}
              className="px-3 py-1.5 rounded-lg border"
            >
              <option value="TODAS">Todas</option>
              {categorias.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>

            <div className="flex items-center rounded-lg border overflow-hidden">
              <span className="pl-2 pr-1 text-slate-400">
                <FaSearch />
              </span>
              <input
                type="text"
                placeholder="Buscar producto..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="px-2 py-1.5 bg-transparent"
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda("")}
                  className="px-2 text-slate-400 hover:text-red-400"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <button
              onClick={obtenerProductos}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border"
            >
              <FaSync />
              Actualizar
            </button>

            <button
              onClick={abrirModalNuevo}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500 text-white"
            >
              <FaPlus />
              Nuevo
            </button>
          </div>
        </div>

        <ProductoTable
          productos={productosFiltrados}
          loading={loading}
          onEditar={abrirModalEditar}
          onEliminar={eliminarProducto}
          onToggleEstado={toggleEstadoProducto}
        />
      </div>

      <ProductoModal
        open={mostrarModal}
        modoEdicion={modoEdicion}
        producto={productoActual}
        categorias={categorias}
        onChange={actualizarCampo}
        onClose={() => setMostrarModal(false)}
        onSave={guardarProducto}
      />
    </>
  );
}

export default ProductoList;